import {DataTable} from "../DataTable/DataTable";
import {ABACPolicyEntity} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {Modal, Text, useMantineTheme} from "@mantine/core";
import {usePagination} from "../DataTable/usePagination";
import {getColumnFilterProps} from "../DataTable/useDataTableFilter";
import {useABACPolicyDataTableFilter} from "../ABACPolicyDataTable/useABACPolicyDataTableFilter";
import {useDataTableSorter} from "../DataTable/useDataTableSorter";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";
import {type ReactNode, useMemo, useState} from "react";
import {DataTableColumn} from "mantine-datatable";
import {sortBy} from "lodash";
import {Editor} from "../../editor/Editor/Editor";

const EMPTY_ARRAY: ABACPolicyEntity[] = []

export type BaseABACPolicyDataTableProps = EntityDataTableProps<ABACPolicyEntity> & {
  renderActions: (record: ABACPolicyEntity, index: number) => ReactNode
}

export const BaseABACPolicyDataTable = (props: BaseABACPolicyDataTableProps) => {
  const {
    records: abacPolicies = EMPTY_ARRAY,
    recordsPerPageOptions,
    renderActions,
    ...rest
  } = props

  const {t} = useTranslation(["common"]);
  const theme = useMantineTheme()

  const [selectedExpr, setSelectedExpr] = useState<string | undefined>(undefined);

  const {filteredRecords, filterConfig} = useABACPolicyDataTableFilter({
    records: abacPolicies
  })

  const sortConfig = useMemo(() => {
    return {
      "name": {
        sortAll: (records, columnAccessor) => {
          return sortBy(records, [function (record) {
            return record.name?.toLowerCase()
          }])
        }
      },
      "description": {
        sortAll: (records, columnAccessor) => {
          return sortBy(records, [function (record) {
            return record.description?.toLowerCase()
          }])
        }
      },
    }
  }, [])

  const {
    sortedRecords,
    sortStatus,
    onSortStatusChange,
  } = useDataTableSorter({
    records: filteredRecords,
    sortStatus: {columnAccessor: 'name', direction: 'asc'},
    sortConfig: sortConfig
  })

  const pagination = usePagination({
    data: sortedRecords,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  const columns = useMemo(() => {
    const values: DataTableColumn<ABACPolicyEntity>[] = [
      {
        ...getColumnFilterProps(filterConfig, "name"),
        title: t("common.entity.abacpolicy.table.column.name", "Name"),
        accessor: 'name',
        width: 400,
        sortable: true,
      },
      {
        ...getColumnFilterProps(filterConfig, "description"),
        title: t("common.entity.abacpolicy.table.column.description", "Description"),
        accessor: 'description',
        sortable: true,
      },
      {
        title: t("common.entity.abacpolicy.table.column.expression", "Expression"),
        accessor: 'expression',
        render: (row) => (
          <Text
            sx={{cursor: 'pointer', color: theme.colors.blue[6]}}
            onClick={() => setSelectedExpr(row.expression)}
          >
            View JSON
          </Text>
        ),
      },
      {
        title: '',
        accessor: "actions",
        width: 100,
        textAlignment: 'right',
        render: renderActions,
      }
    ]

    return values
  }, [filterConfig, t, theme.colors.blue, renderActions])

  return (
    <>
      <DataTable
        {...rest}
        {...(pagination ?? {})}
        sortStatus={sortStatus}
        onSortStatusChange={onSortStatusChange}
        columns={columns}
      />
      {selectedExpr &&
        <Modal
          opened={typeof selectedExpr !== "undefined"}
          onClose={() => setSelectedExpr(undefined)}
          title={t<string>("common.entity.abacpolicy.table.column.expression", "Expression")}
          size="lg"
        >
          <Editor
            height="400px"
            defaultLanguage="json"
            theme={theme.colorScheme === 'dark' ? 'vs-dark' : 'light'}
            value={selectedExpr}
            options={{readOnly: true, minimap: {enabled: false}, domReadOnly: true}}
          />
        </Modal>
      }
    </>
  )
}
